/**
 * Security Settings Page
 * SOC 2 Controls: CC6.1, CC6.2
 * ISO 27001: A.9.4.2, A.9.4.3
 *
 * Allows users to manage MFA, view active sessions, and security settings.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import MFAEnrollment from '@/components/auth/MFAEnrollment';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  Smartphone,
  Key,
  Clock,
  Monitor,
  Trash2,
  Loader2,
  AlertTriangle,
} from 'lucide-react';

interface MFAFactor {
  id: string;
  friendly_name: string;
  factor_type: string;
  status: string;
  created_at: string;
}

interface ActiveSession {
  id: string;
  user_agent: string;
  ip: string;
  created_at: string;
  updated_at: string;
  current: boolean;
}

export default function SecuritySettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [mfaFactors, setMfaFactors] = useState<MFAFactor[]>([]);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disablingMfa, setDisablingMfa] = useState(false);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);

  // Load MFA factors on mount
  useEffect(() => {
    loadMfaStatus();
  }, []);

  const loadMfaStatus = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      const factors = data.totp.map((f) => ({
        id: f.id,
        friendly_name: f.friendly_name || 'Authenticator App',
        factor_type: f.factor_type,
        status: f.status,
        created_at: f.created_at,
      }));

      setMfaFactors(factors);
      setMfaEnabled(factors.some((f) => f.status === 'verified'));
    } catch (err) {
      console.error('Failed to load MFA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMfa = async () => {
    const verifiedFactor = mfaFactors.find((f) => f.status === 'verified');
    if (!verifiedFactor) return;

    setDisablingMfa(true);
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId: verifiedFactor.id });
      if (error) throw error;

      toast({
        title: 'MFA Disabled',
        description: 'Two-factor authentication has been removed from your account.',
      });

      setMfaEnabled(false);
      setMfaFactors([]);
      setDisableDialogOpen(false);
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to disable MFA',
        variant: 'destructive',
      });
    } finally {
      setDisablingMfa(false);
    }
  };

  const handleEnrollmentComplete = () => {
    loadMfaStatus();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="flex-1 space-y-6 p-8">

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Security</h1>
        <p className="text-muted-foreground">
          Manage your account security settings and two-factor authentication
        </p>
      </div>

      {/* Security Status Overview */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">MFA Status</CardTitle>
            {mfaEnabled ? (
              <ShieldCheck className="h-4 w-4 text-green-600" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-yellow-600" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mfaEnabled ? 'Enabled' : 'Disabled'}
            </div>
            <p className="text-xs text-muted-foreground">
              {mfaEnabled
                ? 'Your account is protected'
                : 'Enable for better security'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Password</CardTitle>
            <Key className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">Set</div>
            <p className="text-xs text-muted-foreground">
              Last changed: Unknown
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Sessions</CardTitle>
            <Monitor className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">1</div>
            <p className="text-xs text-muted-foreground">
              Current session only
            </p>
          </CardContent>
        </Card>
      </div>

      {/* MFA Warning if not enabled */}
      {!loading && !mfaEnabled && (
        <Alert variant="destructive" className="bg-yellow-50 border-yellow-200">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-800">
            Two-Factor Authentication Not Enabled
          </AlertTitle>
          <AlertDescription className="text-yellow-700">
            Your account is not protected by two-factor authentication. Enable MFA to
            add an extra layer of security and protect against unauthorized access.
          </AlertDescription>
        </Alert>
      )}

      {/* Two-Factor Authentication */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <div>
                <CardTitle>Two-Factor Authentication</CardTitle>
                <CardDescription>
                  Add an extra layer of security using an authenticator app
                </CardDescription>
              </div>
            </div>
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Badge variant={mfaEnabled ? 'default' : 'secondary'}>
                {mfaEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {mfaEnabled ? (
            <>
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <Smartphone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">Authenticator App</p>
                    <p className="text-sm text-muted-foreground">
                      Added {mfaFactors[0] && formatDate(mfaFactors[0].created_at)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDisableDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Remove
                </Button>
              </div>

              <Separator />

              <div className="space-y-2">
                <h4 className="font-medium">Backup Codes</h4>
                <p className="text-sm text-muted-foreground">
                  If you lose access to your authenticator app, you can use backup
                  codes to sign in.
                </p>
                <Button variant="outline" size="sm">
                  View Backup Codes
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
                <Smartphone className="h-5 w-5 mt-0.5 text-muted-foreground" />
                <div>
                  <p className="font-medium">Authenticator App</p>
                  <p className="text-sm text-muted-foreground">
                    Use an app like Google Authenticator, Authy, or 1Password to
                    generate verification codes.
                  </p>
                </div>
              </div>

              <Button onClick={() => setEnrollDialogOpen(true)}>
                <Shield className="h-4 w-4" />
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            <div>
              <CardTitle>Password</CardTitle>
              <CardDescription>Manage your password settings</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Change Password</p>
              <p className="text-sm text-muted-foreground">
                Update your password regularly for better security
              </p>
            </div>
            <Button variant="outline">Change Password</Button>
          </div>

          <Separator />

          <div className="space-y-2">
            <h4 className="font-medium text-sm">Password Requirements</h4>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>At least 12 characters</li>
              <li>At least one uppercase letter (A-Z)</li>
              <li>At least one lowercase letter (a-z)</li>
              <li>At least one number (0-9)</li>
              <li>At least one special character (!@#$%^&*)</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Session Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            <div>
              <CardTitle>Active Sessions</CardTitle>
              <CardDescription>
                Manage your active sessions across devices
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-3">
              <Monitor className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Current Session</p>
                <p className="text-sm text-muted-foreground">
                  This device · Active now
                </p>
              </div>
            </div>
            <Badge variant="outline" className="text-green-600 border-green-600">
              Current
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* MFA Enrollment Dialog */}
      <MFAEnrollment
        open={enrollDialogOpen}
        onOpenChange={setEnrollDialogOpen}
        onEnrollmentComplete={handleEnrollmentComplete}
      />

      {/* Disable MFA Confirmation Dialog */}
      <AlertDialog open={disableDialogOpen} onOpenChange={setDisableDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Two-Factor Authentication?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the extra security layer from your account. You can
              re-enable it at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableMfa}
              disabled={disablingMfa}
              className="bg-destructive hover:bg-destructive/90"
            >
              {disablingMfa ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable MFA'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
